/**
 * Schema.org Structured Data Components
 * Implements JSON-LD schemas for better SEO, rich results, and AI discovery
 * Updated for AI Optimization (AIO) - November 2024
 */

import { BASE_URL, CONTACT, SEO } from '@/lib/constants';

// ============================================
// Types
// ============================================

interface BreadcrumbItem {
  name: string;
  href: string;
}

interface LodgeSchemaProps {
  name: string;
  description: string;
  image: string;
  url: string;
  address?: {
    locality: string;
    region: string;
    country: string;
  };
  geo?: {
    latitude: number;
    longitude: number;
  };
  amenities?: string[];
  numberOfRooms?: number;
  priceRange?: string;
}

interface AdventureSchemaProps {
  name: string;
  description: string;
  image: string;
  url: string;
  activityType: string;
  location?: string;
  geo?: {
    latitude: number;
    longitude: number;
  };
}

interface FAQItem {
  question: string;
  answer: string;
}

// ============================================
// Organization Schema (Global - for homepage)
// Enhanced for AI discovery
// ============================================

export function OrganizationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    '@id': `${BASE_URL}/#organization`,
    name: SEO.siteName,
    alternateName: ['Apex Andes', 'Apex Heliski', 'Apex Chile'],
    description: 'Premier luxury adventure travel company specializing in heli-skiing, fly fishing, and exclusive lodges in the Chilean Andes and Patagonia.',
    url: BASE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/logo.svg`,
      width: 200,
      height: 60,
    },
    image: `${BASE_URL}/og-image.png`,
    telephone: CONTACT.whatsapp,
    email: CONTACT.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '330 Belleview Ave',
      addressLocality: 'Crested Butte',
      addressRegion: 'CO',
      postalCode: '81224',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: -33.3547,
      longitude: -70.2451,
    },
    areaServed: [
      {
        '@type': 'Country',
        name: 'Chile',
      },
      {
        '@type': 'Place',
        name: 'Patagonia',
      },
      {
        '@type': 'Place',
        name: 'Chilean Andes',
      },
    ],
    sameAs: [
      'https://www.instagram.com/apexexperience/',
      'https://www.facebook.com/apexexperience',
      'https://twitter.com/apexexperience',
    ],
    foundingDate: '2020',
    knowsAbout: [
      'Heli-skiing',
      'Fly fishing',
      'Ski touring',
      'Backcountry skiing',
      'Cat skiing',
      'Luxury travel',
      'Adventure tourism',
      'Chilean Andes',
      'Patagonia',
      'Luxury lodges',
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Adventure Experiences',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Heli-Skiing',
            description: 'Private helicopter access to 10,000+ vertical feet of untouched powder',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Fly Fishing',
            description: 'Trophy trout fishing in Patagonia\'s legendary rivers',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Luxury Lodge Accommodation',
            description: 'Six exclusive lodges in the Chilean Andes and Patagonia',
          },
        },
      ],
    },
    priceRange: '$$$$$',
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
    slogan: 'Unprecedented adventures at premier lodges',
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// ============================================
// Website Schema (Global)
// ============================================

export function WebsiteSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${BASE_URL}/#website`,
    url: BASE_URL,
    name: SEO.siteName,
    description: SEO.defaultDescription,
    publisher: {
      '@id': `${BASE_URL}/#organization`,
    },
    // SearchAction removed - no search page exists on this site
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// ============================================
// Breadcrumb Schema
// ============================================

export function BreadcrumbSchema({ items }: { items: BreadcrumbItem[] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${BASE_URL}${item.href}`,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// ============================================
// Lodge/Accommodation Schema
// Enhanced for AI discovery with detailed amenities
// ============================================

export function LodgeSchema({
  name,
  description,
  image,
  url,
  address,
  geo,
  amenities = [],
  numberOfRooms,
  priceRange = '$$$$$',
}: LodgeSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LodgingBusiness',
    '@id': `${BASE_URL}${url}#lodge`,
    name,
    description,
    image: image.startsWith('http') ? image : `${BASE_URL}${image}`,
    url: `${BASE_URL}${url}`,
    telephone: CONTACT.whatsapp,
    priceRange,
    starRating: {
      '@type': 'Rating',
      ratingValue: '5',
    },
    address: address
      ? {
          '@type': 'PostalAddress',
          addressLocality: address.locality,
          addressRegion: address.region,
          addressCountry: address.country,
        }
      : undefined,
    geo: geo
      ? {
          '@type': 'GeoCoordinates',
          latitude: geo.latitude,
          longitude: geo.longitude,
        }
      : undefined,
    amenityFeature: amenities.map((amenity) => ({
      '@type': 'LocationFeatureSpecification',
      name: amenity,
      value: true,
    })),
    numberOfRooms: numberOfRooms,
    petsAllowed: false,
    checkinTime: '15:00',
    checkoutTime: '11:00',
    availableLanguage: ['English', 'Spanish'],
    parentOrganization: {
      '@id': `${BASE_URL}/#organization`,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// ============================================
// Adventure/Tour Schema
// Enhanced as TouristAttraction for AI discovery
// ============================================

export function AdventureSchema({
  name,
  description,
  image,
  url,
  activityType,
  location,
  geo,
}: AdventureSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'TouristAttraction',
    '@id': `${BASE_URL}${url}#attraction`,
    name,
    description,
    image: image.startsWith('http') ? image : `${BASE_URL}${image}`,
    url: `${BASE_URL}${url}`,
    touristType: ['Adventure Traveler', 'Ski Enthusiast', 'Luxury Traveler', activityType],
    geo: geo
      ? {
          '@type': 'GeoCoordinates',
          latitude: geo.latitude,
          longitude: geo.longitude,
        }
      : {
          '@type': 'GeoCoordinates',
          latitude: -33.35,
          longitude: -70.25,
        },
    isAccessibleForFree: false,
    publicAccess: false,
    availableLanguage: ['English', 'Spanish'],
    openingHours: 'Mo-Su 07:00-18:00',
    provider: {
      '@id': `${BASE_URL}/#organization`,
    },
    ...(location && {
      containedInPlace: {
        '@type': 'Place',
        name: location,
      },
    }),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// ============================================
// FAQ Schema
// ============================================

export function FAQSchema({ faqs }: { faqs: FAQItem[] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// ============================================
// Local Business Schema (for Contact page)
// ============================================

export function LocalBusinessSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    '@id': `${BASE_URL}/#organization`,
    name: SEO.siteName,
    description: SEO.defaultDescription,
    url: BASE_URL,
    telephone: CONTACT.whatsapp,
    email: CONTACT.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Santiago',
      addressRegion: 'Región Metropolitana',
      addressCountry: 'CL',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: -33.4489,
      longitude: -70.6693,
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: CONTACT.whatsapp,
      contactType: 'customer service',
      availableLanguage: ['English', 'Spanish'],
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// ============================================
// Review/Testimonial Aggregate Schema
// ============================================

export function AggregateRatingSchema({
  ratingValue,
  reviewCount,
}: {
  ratingValue: number;
  reviewCount: number;
}) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    '@id': `${BASE_URL}/#organization`,
    name: SEO.siteName,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: ratingValue.toFixed(1),
      bestRating: '5',
      worstRating: '1',
      reviewCount,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// ============================================
// Individual Review Schema
// Critical for AI discovery - LLMs prefer individual reviews
// ============================================

export interface ReviewSchemaProps {
  reviewerName: string;
  reviewerLocation?: string;
  reviewDate: string;
  ratingValue: number;
  reviewBody: string;
  itemReviewed: string;
}

export function ReviewSchema({
  reviewerName,
  reviewerLocation,
  reviewDate,
  ratingValue,
  reviewBody,
  itemReviewed,
}: ReviewSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: reviewerName,
      ...(reviewerLocation && {
        address: {
          '@type': 'PostalAddress',
          addressLocality: reviewerLocation,
        },
      }),
    },
    datePublished: reviewDate,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: ratingValue.toFixed(1),
      bestRating: '5',
      worstRating: '1',
    },
    reviewBody,
    itemReviewed: {
      '@type': 'TravelAgency',
      '@id': `${BASE_URL}/#organization`,
      name: SEO.siteName,
      description: itemReviewed,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// ============================================
// Multiple Reviews Schema (for testimonials page)
// Combines aggregate rating with individual reviews
// ============================================

export interface TestimonialReview {
  name: string;
  location: string;
  date: string;
  rating: number;
  text: string;
  adventure: string;
}

export function TestimonialsPageSchema({
  reviews,
}: {
  reviews: TestimonialReview[];
}) {
  const avgRating =
    reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    '@id': `${BASE_URL}/#organization`,
    name: SEO.siteName,
    description:
      'Premier luxury adventure travel company specializing in heli-skiing, fly fishing, and exclusive lodges in Chile.',
    url: BASE_URL,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: avgRating.toFixed(1),
      bestRating: '5',
      worstRating: '1',
      reviewCount: reviews.length,
    },
    review: reviews.map((review) => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: review.name,
        address: {
          '@type': 'PostalAddress',
          addressLocality: review.location,
        },
      },
      datePublished: parseReviewDate(review.date),
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.rating.toFixed(1),
        bestRating: '5',
        worstRating: '1',
      },
      reviewBody: review.text,
      name: review.adventure,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Helper to convert "July 2024" format to ISO date
function parseReviewDate(dateStr: string): string {
  const months: Record<string, string> = {
    January: '01',
    February: '02',
    March: '03',
    April: '04',
    May: '05',
    June: '06',
    July: '07',
    August: '08',
    September: '09',
    October: '10',
    November: '11',
    December: '12',
  };
  const [month, year] = dateStr.split(' ');
  return `${year}-${months[month] || '01'}-15`;
}

// ============================================
// Page-Specific FAQ Data for AI Discovery
// ============================================

export const HELI_SKIING_FAQS: FAQItem[] = [
  {
    question: 'How much does heli-skiing in Chile cost?',
    answer:
      'Heli-skiing packages in the Chilean Andes typically range from $8,000 to $15,000 per person for a week-long experience. This includes luxury lodge accommodation, IFMGA-certified guides, private helicopter access, all equipment, gourmet meals, and airport transfers. Contact Apex Experience for customized pricing based on your group size and preferences.',
  },
  {
    question: 'What is the best time for heli-skiing in Chile?',
    answer:
      'The optimal heli-skiing season in Chile runs from July through September, when the Andes receive consistent snowfall with dry, light powder conditions. June and October can also offer excellent skiing depending on seasonal variations. The Southern Hemisphere winter means you can ski during Northern Hemisphere summer.',
  },
  {
    question: 'Do I need experience to go heli-skiing in Chile?',
    answer:
      'Yes, heli-skiing requires advanced skiing ability. You should be comfortable skiing black diamond runs in varied snow conditions including powder, crud, and steeper terrain. Our IFMGA-certified guides assess each guest\'s ability on day one and carefully select appropriate terrain. We recommend at least 5+ years of skiing experience with regular off-piste skiing.',
  },
  {
    question: 'How many runs per day on a Chilean heli-ski trip?',
    answer:
      'On average, guests complete 6-8 helicopter-accessed runs per day, depending on conditions and fitness levels. Each run typically offers 1,500-3,000 vertical feet of untracked powder. Weather permitting, you can accumulate 10,000+ vertical feet daily. Our flexible approach means we maximize skiing time based on snow and visibility conditions.',
  },
  {
    question: 'What safety equipment is provided for heli-skiing?',
    answer:
      'All guests receive complete avalanche safety equipment: digital transceivers, probes, shovels, and ABS airbag backpacks. We also provide satellite communication devices, first aid kits, and helicopter rescue insurance. All guides are IFMGA-certified with extensive avalanche training (AIARE Level 3+) and wilderness first responder certifications.',
  },
];

export const FLY_FISHING_FAQS: FAQItem[] = [
  {
    question: 'What is the best season for fly fishing in Patagonia?',
    answer:
      'The fly fishing season in Chilean Patagonia runs from November through April. Peak season is December through March when conditions are most consistent, hatches are prolific, and weather is most favorable. Early season (November) offers aggressive fish after winter, while late season (April) brings larger trophy trout before spawning.',
  },
  {
    question: 'What fish species can I catch in Patagonia?',
    answer:
      'Chilean Patagonia offers exceptional fishing for Brown Trout (averaging 18-24 inches, with trophy fish over 30 inches), Rainbow Trout, and Brook Trout. The Yelcho River and surrounding waters are famous for producing some of South America\'s largest wild trout. All fishing is catch-and-release to preserve these pristine fisheries.',
  },
  {
    question: 'What is included in an Apex Patagonia fly fishing trip?',
    answer:
      'Our all-inclusive packages include luxury lodge accommodation at Apex Yelcho Patagonia, expert bilingual guides (1 guide per 2 anglers), all fishing equipment (Sage/Simms gear available), gourmet meals with Chilean wines, daily fishing on private waters, boat access, airport transfers from Puerto Montt, and comprehensive fishing licenses.',
  },
  {
    question: 'Do I need experience for fly fishing in Patagonia?',
    answer:
      'We welcome anglers of all skill levels. Beginners receive casting instruction and guided techniques from our expert team, while experienced fly fishers can target technical trophy fish. Our guides adapt to your skill level and goals, whether you\'re learning to cast or perfecting dry fly presentations for selective brown trout.',
  },
  {
    question: 'What makes Chilean Patagonia fly fishing unique?',
    answer:
      'Chilean Patagonia offers pristine, uncrowded waters with exceptional wild trout populations. The Yelcho region features crystal-clear rivers fed by glacial lakes, minimal fishing pressure compared to Argentine waters, and diverse fishing from small streams to large river systems. The scenery of snow-capped volcanoes and ancient forests is unmatched.',
  },
];

export const APEX_YELCHO_FAQS: FAQItem[] = [
  {
    question: 'Where is Apex Yelcho Patagonia located?',
    answer:
      'Apex Yelcho Patagonia is located on the shores of Lake Yelcho in Chile\'s Northern Patagonia region (Los Lagos Region). The lodge is approximately 45 minutes from Chaitén and 4 hours from Puerto Montt airport. The remote location on this 60-mile glacial lake provides access to world-class fishing waters with minimal crowds.',
  },
  {
    question: 'How do I get to Apex Yelcho Patagonia?',
    answer:
      'Most guests fly into Puerto Montt (PMC) airport, where our team arranges scenic ground transfers (4 hours) or optional charter flights (45 minutes) to the lodge. We handle all logistics including customs assistance, equipment transport, and communication with international flights. The journey itself showcases Patagonia\'s stunning landscapes.',
  },
  {
    question: 'What is the accommodation like at Apex Yelcho?',
    answer:
      'The lodge features spacious private rooms with lake views, en-suite bathrooms, heated floors, and premium bedding. Common areas include a great room with fireplace, fly-tying station, full-service bar, and outdoor terraces overlooking Lake Yelcho. Maximum 8 guests ensures personalized service and exclusive waters access.',
  },
  {
    question: 'What fishing waters can I access from Apex Yelcho?',
    answer:
      'Guests have exclusive access to the legendary Yelcho River (famous for 10lb+ brown trout), Lake Yelcho, Futaleufú River system, and numerous private streams and spring creeks. Daily fishing locations are selected based on conditions, hatches, and guest preferences. Both wade fishing and drift boat fishing are available.',
  },
  {
    question: 'Can I combine fishing with other activities at Apex Yelcho?',
    answer:
      'Absolutely! Non-fishing days or afternoons can include kayaking on Lake Yelcho, hiking to nearby waterfalls and viewpoints, wildlife watching (condors, huemul deer, pumas), visits to Pumalín National Park, local hot springs, or simply relaxing at the lodge with spa treatments. We accommodate diverse group interests.',
  },
];

export const PATAGONIA_LODGE_FAQS: FAQItem[] = [
  {
    question: 'How much does a stay at Apex Patagonia Lodge cost?',
    answer:
      'A week-long fly fishing package at Apex Patagonia Lodge typically ranges from $6,500 to $9,500 per person, depending on the season and group size. This all-inclusive rate covers luxury accommodation, expert bilingual guides, all meals with Chilean wines, 12km of private river access, fishing equipment, and airport transfers from Puerto Montt.',
  },
  {
    question: 'What is the best season to visit Patagonia Lodge for fly fishing?',
    answer:
      'The prime fly fishing season at Patagonia Lodge runs from November through April. December through March offers the most consistent conditions with prolific hatches and ideal weather. Early season (November) features aggressive post-winter fish, while late season (April) presents opportunities for trophy-sized trout before spawning.',
  },
  {
    question: 'What is included in a stay at Apex Patagonia Lodge?',
    answer:
      'Our all-inclusive packages include luxury riverside accommodation, gourmet meals prepared by our private chef with regional wines, expert bilingual fishing guides (1:2 ratio), exclusive access to 12km of private river, premium fishing equipment (Sage/Simms), fly-tying station, airport transfers, and fishing licenses. Non-fishing activities are also available.',
  },
  {
    question: 'How do I get to Apex Patagonia Lodge lodge?',
    answer:
      'Guests fly into Puerto Montt (PMC) airport, where our team arranges ground transfers (approximately 3.5 hours) through scenic Patagonian landscapes. Alternatively, helicopter transfers can be arranged for faster access (45 minutes). We coordinate all logistics including equipment transport and customs assistance for international guests.',
  },
  {
    question: 'Do I need fly fishing experience for Patagonia Lodge?',
    answer:
      'Patagonia Lodge welcomes anglers of all skill levels. Beginners receive personalized casting instruction and guided techniques from our expert team, while experienced fly fishers can pursue technical trophy trout on our private waters. Our guides adapt to your ability and goals, ensuring a rewarding experience regardless of your background.',
  },
];

export const PREMIUM_LODGE_FAQS: FAQItem[] = [
  {
    question: 'What makes Apex Premium Lodge different from other ski lodges?',
    answer:
      'Apex Premium Lodge offers an ultra-luxury all-inclusive experience at 3,000m elevation in the Chilean Andes. Distinguishing features include private helicopter transfers, dedicated butler service, a 1:2 staff-to-guest ratio, world-class spa, sommelier-curated wine cellar, cinema room, and exclusive access to pristine backcountry terrain with IFMGA-certified guides.',
  },
  {
    question: 'How much does a stay at Apex Premium Lodge cost?',
    answer:
      'Weekly packages at Apex Premium Lodge range from $12,000 to $18,000 per person, reflecting our ultra-luxury positioning. This includes private helicopter transfers, all-inclusive accommodation with butler service, gourmet meals with premium wines, unlimited guided skiing/riding, spa treatments, and personalized concierge service throughout your stay.',
  },
  {
    question: 'What activities are available at Apex Premium Lodge?',
    answer:
      'Beyond exceptional heli-skiing and ski touring, guests enjoy cat skiing, backcountry splitboarding, snowshoeing, and ice climbing. Non-ski activities include spa treatments, yoga sessions, wine tastings with our sommelier, gourmet cooking classes, and private cinema screenings. We customize each day to your preferences.',
  },
  {
    question: 'When is the best time to visit Apex Premium Lodge?',
    answer:
      'The optimal season runs from July through September, with peak powder conditions typically in August. June offers early season freshness with fewer guests, while October provides spring skiing with longer days and corn snow. Our high elevation (3,000m) ensures excellent snow conditions throughout the Chilean winter.',
  },
  {
    question: 'How many guests does Apex Premium Lodge accommodate?',
    answer:
      'Apex Premium Lodge accommodates a maximum of 16 guests in 8 luxury suites, ensuring an intimate and exclusive experience. This limited capacity allows for personalized service with our 1:2 staff ratio, minimal lift lines, and maximum vertical feet per day. Private buyouts are available for groups seeking complete exclusivity.',
  },
];

export const PARVA_CAVE_FAQS: FAQItem[] = [
  {
    question: 'What is Apex Parva Cave?',
    answer:
      'Apex Parva Cave is a unique cave-inspired luxury retreat nestled in La Parva ski resort in the Chilean Andes. This intimate accommodation features true ski-in/ski-out access, an outdoor hot tub with mountain views, cozy fireplace lounges, and authentic mountain hospitality. It serves as an ideal base for skiing, ski touring, and backcountry adventures.',
  },
  {
    question: 'Where is Apex Parva Cave located?',
    answer:
      'Parva Cave is located in La Parva, one of three interconnected ski resorts in the Tres Valles area, just 50 minutes from Santiago international airport. At 2,750m elevation, it offers direct access to over 1,500 acres of skiable terrain plus extensive backcountry touring options in the Andes. The proximity to Santiago makes it uniquely accessible.',
  },
  {
    question: 'What skiing activities are available from Parva Cave?',
    answer:
      'From Parva Cave, guests enjoy lift-served skiing at La Parva (plus neighboring Valle Nevado and El Colorado with shared passes), guided backcountry ski touring, splitboarding adventures, and cat skiing excursions. Our IFMGA-certified guides access terrain ranging from gentle bowls to steep couloirs, suitable for intermediate to expert skiers.',
  },
  {
    question: 'What is the best season to stay at Parva Cave?',
    answer:
      'The ski season at La Parva typically runs from mid-June through early October. July and August offer the most reliable snow conditions with frequent powder days. June and September-October provide excellent skiing with fewer crowds. The resort\'s high elevation ensures quality snow throughout the season.',
  },
  {
    question: 'What is the accommodation style at Parva Cave?',
    answer:
      'Parva Cave accommodates up to 8 guests in 4 cozy rooms with mountain-inspired decor. The cave-like architecture creates a warm, intimate atmosphere with stone walls, wooden beams, and panoramic windows. Amenities include an outdoor hot tub, boot warmers, ski storage, equipment room, fireplace lounge, and home-cooked meals by our resident chef.',
  },
];

export const SKI_TOURING_FAQS: FAQItem[] = [
  {
    question: 'What is ski touring in the Chilean Andes?',
    answer:
      'Ski touring (also called ski mountaineering or randonee) involves ascending mountains on skis using climbing skins, then skiing down untracked terrain. In the Chilean Andes, this means accessing remote peaks, glaciers, and volcanic slopes far from lift-served areas. It combines the physical satisfaction of earning your turns with the reward of pristine powder descents.',
  },
  {
    question: 'How is ski touring different from heli-skiing?',
    answer:
      'While heli-skiing uses helicopters to access terrain quickly, ski touring involves ascending under your own power using climbing skins and specialized bindings. Ski touring offers a more intimate, self-reliant mountain experience with unlimited vertical potential (no flight restrictions), lower environmental impact, and the satisfaction of earning every descent. Many guests combine both activities.',
  },
  {
    question: 'What fitness level is required for ski touring in Chile?',
    answer:
      'Ski touring requires good cardiovascular fitness as you\'ll be skinning uphill for 2-4 hours per run. We recommend being able to hike uphill for 3+ hours comfortably. Advanced skiing ability (black diamond proficiency) is essential for descents. Our guides assess abilities on day one and select appropriate objectives, ranging from gentle volcanoes to technical couloirs.',
  },
  {
    question: 'When is the best season for ski touring in Chile?',
    answer:
      'The Chilean ski touring season runs from June through October, with optimal conditions from July to September. Early season (June-July) offers deep powder on lower elevations, while late season (September-October) provides stable spring conditions ideal for high-altitude volcano ascents. The Southern Hemisphere timing perfectly complements Northern Hemisphere summers.',
  },
  {
    question: 'What is included in an Apex ski touring package?',
    answer:
      'Our ski touring packages include luxury lodge accommodation, IFMGA-certified mountain guides, all touring equipment (skis, boots, skins, poles), complete avalanche safety gear (transceivers, probes, shovels, airbags), daily breakfast and lunch, transportation to trailheads, and mountain rescue insurance. Multi-day hut-based traverses are also available.',
  },
];

// ============================================
// Global FAQ Data for AI Discovery
// ============================================

export const GLOBAL_FAQS: FAQItem[] = [
  {
    question: 'What is the best time to go heli-skiing in Chile?',
    answer: 'The best time for heli-skiing in Chile is from July to September, when the Andes receive consistent snowfall and conditions are optimal. June and October can also offer excellent skiing depending on the season.',
  },
  {
    question: 'Do I need prior skiing experience for heli-skiing?',
    answer: 'Yes, heli-skiing requires advanced skiing ability. You should be comfortable skiing black diamond runs in varied snow conditions. Our IFMGA-certified guides assess each guest\'s ability and tailor the terrain accordingly.',
  },
  {
    question: 'What is included in an Apex Experience package?',
    answer: 'Our all-inclusive packages include luxury lodge accommodation, gourmet meals with wine pairings, professional guides, all equipment, airport transfers, and the adventure activity itself (helicopter flights for heli-skiing, boats for fishing, etc.).',
  },
  {
    question: 'Where are Apex Experience lodges located?',
    answer: 'Our lodges are located throughout the Chilean Andes and Patagonia. We have ski lodges at Valle Nevado and La Parva near Santiago, and fishing lodges in Northern Patagonia on Lake Yelcho and surrounding rivers.',
  },
  {
    question: 'What is the best season for fly fishing in Patagonia?',
    answer: 'The fly fishing season in Chilean Patagonia runs from November through April. Peak season is December through March when conditions are most consistent and hatches are prolific.',
  },
  {
    question: 'How much does heli-skiing in Chile cost?',
    answer: 'Heli-skiing packages in Chile typically range from $8,000 to $15,000 per person for a week-long experience, depending on the lodge selection and number of helicopter flights. Contact us for customized pricing.',
  },
  {
    question: 'What makes Chilean Andes skiing unique?',
    answer: 'The Chilean Andes offer incredible dry powder snow, vast terrain with minimal crowds, and the ability to ski during the Northern Hemisphere summer (June-October). Combined with world-class lodges and experienced guides, it\'s a premier skiing destination.',
  },
  {
    question: 'Can I combine skiing and fishing in one trip?',
    answer: 'Yes! Our Patagonia lodges offer the unique opportunity to combine heli-skiing with fly fishing. The shoulder seasons (June and October-November) are ideal for experiencing both activities.',
  },
];

// ============================================
// Global FAQ Schema Component
// Use on homepage or key landing pages
// ============================================

export function GlobalFAQSchema() {
  return <FAQSchema faqs={GLOBAL_FAQS} />;
}

// ============================================
// Lodges Index FAQs
// ============================================

export const LODGES_INDEX_FAQS: FAQItem[] = [
  {
    question: 'What types of lodges does Apex Experience offer?',
    answer: 'Apex Experience offers six exclusive lodges across Chile: luxury ski lodges in the Andes near Santiago, remote fly fishing lodges in Patagonia, and unique accommodations like cave-style retreats. Each lodge provides all-inclusive packages with gourmet cuisine and expert guides.'
  },
  {
    question: 'How do I choose the right Apex lodge for my trip?',
    answer: 'Choose based on your primary activity: For skiing, select Premium Lodge, Parva Cave, or Mountain Penthouse near Santiago. For fly fishing, choose Patagonia Lodge or Yelcho Patagonia in southern Chile. For a lakeside retreat, consider Lakefront Serenity. Our team can help match your preferences.'
  },
  {
    question: 'Are Apex lodges all-inclusive?',
    answer: 'Yes, all Apex lodges offer all-inclusive packages that typically include accommodation, gourmet meals, premium beverages, guide services, equipment, and transportation from pickup points. Helicopter flights and special activities may be additional depending on the package.'
  },
  {
    question: 'What is the typical group size at Apex lodges?',
    answer: 'Apex lodges accommodate small exclusive groups, typically 6-12 guests maximum. This ensures personalized service, uncrowded experiences, and flexibility in daily activities. Private buyouts are available for families or corporate groups seeking complete exclusivity.'
  },
  {
    question: 'When should I book an Apex lodge?',
    answer: 'Book 6-12 months in advance for peak seasons: June-September for ski lodges, November-April for fishing lodges. Last-minute availability occasionally exists. Contact us early to secure your preferred dates and customize your experience.'
  }
];

// ============================================
// Adventures Index FAQs
// ============================================

export const ADVENTURES_INDEX_FAQS: FAQItem[] = [
  {
    question: 'What guided adventures does Apex Experience offer in Chile?',
    answer: 'Apex Experience offers six signature adventures: heli-skiing with 100,000+ acres of terrain, backcountry ski touring, cat skiing, resort skiing at top Chilean resorts, world-class Patagonian fly fishing, and summer water skiing on pristine lakes.'
  },
  {
    question: 'Do I need prior experience for Apex adventures?',
    answer: 'Experience requirements vary by activity. Heli-skiing and ski touring require advanced skiing ability. Resort skiing welcomes all levels with instruction available. Fly fishing accommodates beginners to experts with professional guides. We match experiences to your skill level.'
  },
  {
    question: 'What is included in an Apex guided adventure package?',
    answer: 'Packages typically include luxury lodge accommodation, gourmet meals, expert bilingual guides, safety equipment, ground transportation, and the primary activity. Helicopter flights, special equipment, and add-on adventures may be separate. Each package is customizable.'
  },
  {
    question: 'What is the best time of year for adventures in Chile?',
    answer: 'Chile offers year-round adventures: June-October for skiing (powder peaks July-August), November-April for fly fishing (prime December-March), and December-March for water skiing. The reversed seasons make Chile perfect when Northern Hemisphere slopes close.'
  },
  {
    question: 'How do I book a guided adventure with Apex?',
    answer: 'Contact Apex Experience through our website or email to discuss your dates, group size, and preferences. Our team will create a customized itinerary, handle all logistics, and ensure every detail is arranged. A deposit secures your booking with full payment due before arrival.'
  }
];

// ============================================
// Cat Skiing FAQs
// ============================================

export const CAT_SKIING_FAQS: FAQItem[] = [
  {
    question: 'What is cat skiing and how does it work?',
    answer: 'Cat skiing uses a specialized snow cat vehicle to transport skiers to untracked powder terrain. Unlike heli-skiing, cat skiing offers unlimited vertical with a dedicated cat accessing terrain all day. It\'s a more affordable alternative to helicopter access while still providing pristine backcountry skiing.'
  },
  {
    question: 'How much does cat skiing in Chile cost?',
    answer: 'Cat skiing packages in Chile typically range from $4,000-$8,000 for a week, including accommodation, meals, guides, and unlimited cat laps. This is generally 30-40% less expensive than heli-skiing while offering similar quality terrain and snow conditions.'
  },
  {
    question: 'What skill level is required for cat skiing?',
    answer: 'Cat skiing requires strong intermediate to advanced skiing ability. You should be comfortable skiing varied terrain including steep pitches, trees, and variable snow conditions. No backcountry experience is necessary as professional guides handle all safety and navigation.'
  },
  {
    question: 'What is the best time for cat skiing in Chile?',
    answer: 'The prime cat skiing season in Chile runs from late June through early October, with peak conditions typically in July and August. The reversed Southern Hemisphere winter means you can ski powder while Northern Hemisphere resorts are closed for summer.'
  },
  {
    question: 'How many runs can I expect per day cat skiing?',
    answer: 'A typical cat skiing day includes 10-15 runs covering 15,000-25,000 vertical feet. The snow cat can access terrain continuously throughout the day, maximizing your ski time in fresh powder without helicopter wait times or weather delays.'
  }
];

// ============================================
// Resort Skiing FAQs
// ============================================

export const RESORT_SKIING_FAQS: FAQItem[] = [
  {
    question: 'What ski resorts can I visit with Apex Experience?',
    answer: 'Apex Experience provides access to Chile\'s premier ski resorts including Valle Nevado, La Parva, El Colorado, Portillo, and Nevados de Chillan. Our packages include VIP lift access, equipment rentals, and expert guides who know every run and hidden powder stash.'
  },
  {
    question: 'When is the best time to ski at Chilean resorts?',
    answer: 'Chilean ski resorts operate from mid-June through early October. Peak season with the best snow conditions runs from mid-July through August. September offers warmer weather and corn snow, ideal for spring skiing conditions.'
  },
  {
    question: 'Do I need to be an expert to ski Chilean resorts?',
    answer: 'No, Chilean resorts cater to all ability levels from beginners to experts. Resorts like Valle Nevado and Portillo offer extensive groomed terrain, ski schools, and beginner areas alongside challenging expert terrain and off-piste opportunities.'
  },
  {
    question: 'What is included in an Apex resort skiing package?',
    answer: 'Our resort packages include luxury accommodation, daily transfers to resorts, lift tickets, optional equipment rental, and guide services. We handle all logistics so you can focus on skiing. Private lessons and off-piste guiding are available additions.'
  },
  {
    question: 'How do Chilean ski resorts compare to North American or European resorts?',
    answer: 'Chilean resorts offer uncrowded slopes, consistent dry powder, and terrain rivaling top global destinations. Valle Nevado alone has over 7,000 acres of skiable terrain. The reversed seasons mean you can extend your ski year with Southern Hemisphere powder.'
  }
];

// ============================================
// Water Skiing FAQs
// ============================================

export const WATER_SKIING_FAQS: FAQItem[] = [
  {
    question: 'Where does Apex offer water skiing experiences?',
    answer: 'Apex Experience offers water skiing on pristine Patagonian lakes including Lago Llanquihue, Lago Todos los Santos, and private lake access near our lodges. These glacier-fed lakes provide glass-calm morning conditions and stunning volcanic backdrops.'
  },
  {
    question: 'What is the best season for water skiing in Chile?',
    answer: 'The water skiing season in Chile runs from December through March during the Southern Hemisphere summer. January and February offer the warmest temperatures and most consistent calm water conditions, typically in early mornings and late afternoons.'
  },
  {
    question: 'What water skiing equipment is provided?',
    answer: 'Apex provides professional-grade ski boats, a full range of water skis, wakeboards, tubes, and all safety equipment. Our experienced boat drivers and spotters ensure safe, enjoyable sessions for all skill levels from beginners to advanced slalom skiers.'
  },
  {
    question: 'Can I combine water skiing with other activities?',
    answer: 'Absolutely! Water skiing pairs perfectly with our fly fishing programs and lodge stays. Many guests enjoy morning fishing, afternoon water sports, and evening relaxation at our lakeside lodges. We create custom multi-activity itineraries.'
  },
  {
    question: 'Do I need prior water skiing experience?',
    answer: 'No experience necessary. Our instructors teach beginners from first attempts through advanced techniques. We also offer wakeboarding, tubing, and other water activities for those who prefer alternatives to traditional water skiing.'
  }
];

// ============================================
// Why Apex FAQs
// ============================================

export const WHY_APEX_FAQS: FAQItem[] = [
  {
    question: 'What makes Apex Experience different from other adventure travel companies?',
    answer: 'Apex Experience combines 20+ years of local expertise with exclusive access to private terrain, helicopter operations, and remote lodges. Our bilingual guides, small group sizes (maximum 12 guests), and all-inclusive luxury approach create truly personalized adventures unavailable elsewhere.'
  },
  {
    question: 'How long has Apex Experience been operating?',
    answer: 'Apex Experience has been crafting premium adventure experiences in Chile since 2003. Our team includes founding members who pioneered heli-skiing in the Chilean Andes and established partnerships with the best lodges and operators throughout Patagonia.'
  },
  {
    question: 'Is Apex Experience a licensed tour operator?',
    answer: 'Yes, Apex Experience is a fully licensed Chilean tour operator with all required certifications, insurance, and safety protocols. We hold permits for helicopter operations, backcountry guiding, and fishing in protected waters throughout Chile.'
  },
  {
    question: 'What safety measures does Apex have in place?',
    answer: 'Safety is paramount at Apex. All backcountry activities include avalanche-trained guides with rescue equipment, satellite communication, and evacuation protocols. Our helicopter operations meet international standards with experienced pilots and regular maintenance checks.'
  },
  {
    question: 'Can Apex accommodate special dietary or accessibility needs?',
    answer: 'Yes, we accommodate dietary restrictions, allergies, and many accessibility needs with advance notice. Our lodges can prepare vegetarian, vegan, gluten-free, and other special menus. Contact us during booking to discuss specific requirements.'
  }
];

// ============================================
// HowTo Schema Component
// ============================================

interface HowToStep {
  name: string;
  text: string;
  url?: string;
}

interface HowToSchemaProps {
  name: string;
  description: string;
  totalTime?: string;
  estimatedCost?: { currency: string; value: string };
  steps: HowToStep[];
  image?: string;
}

export function HowToSchema({ name, description, totalTime, estimatedCost, steps, image }: HowToSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    ...(totalTime && { totalTime }),
    ...(estimatedCost && {
      estimatedCost: {
        '@type': 'MonetaryAmount',
        currency: estimatedCost.currency,
        value: estimatedCost.value
      }
    }),
    ...(image && { image }),
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      ...(step.url && { url: step.url })
    }))
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// ============================================
// HowTo Data Constants
// ============================================

export const HOWTO_BOOK_HELISKI = {
  name: 'How to Book a Heli-Skiing Trip in Chile',
  description: 'Step-by-step guide to planning and booking your Chilean Andes heli-skiing adventure with Apex Experience.',
  totalTime: 'P14D',
  estimatedCost: { currency: 'USD', value: '8000-15000' },
  steps: [
    { name: 'Choose Your Dates', text: 'Select your preferred week between June and October. Peak powder conditions occur July-August. Book 6-12 months ahead for best availability.' },
    { name: 'Contact Apex Experience', text: 'Reach out via our contact form or email with your dates, group size, and skiing experience level. Our team responds within 24 hours.' },
    { name: 'Customize Your Package', text: 'Work with our team to select your lodge, number of helicopter hours, and any additional activities like ski touring or resort days.' },
    { name: 'Confirm with Deposit', text: 'Secure your booking with a 50% deposit. Review the detailed itinerary and packing list we provide.' },
    { name: 'Prepare for Your Trip', text: 'Arrange flights to Santiago, complete any required fitness preparation, and gather your ski gear. We handle all in-country logistics.' },
    { name: 'Arrive and Ski', text: 'Meet our team in Santiago for transfer to your lodge. Receive safety briefing, meet your guides, and begin your heli-skiing adventure.' }
  ]
};

export const HOWTO_BOOK_FISHING = {
  name: 'How to Plan a Fly Fishing Trip to Patagonia',
  description: 'Complete guide to booking your Patagonian fly fishing adventure with Apex Experience lodges.',
  totalTime: 'P21D',
  estimatedCost: { currency: 'USD', value: '6000-12000' },
  steps: [
    { name: 'Select Your Season', text: 'Choose dates between November and April for the Southern Hemisphere summer. December-March offers prime conditions for trout and salmon.' },
    { name: 'Choose Your Lodge', text: 'Select between Patagonia Lodge for intimate river access or Yelcho Patagonia for remote wilderness fishing with helicopter access to distant waters.' },
    { name: 'Contact Our Team', text: 'Submit an inquiry with your preferred dates, fishing experience, and any special requests. We customize every trip to your goals.' },
    { name: 'Plan Your Fishing Program', text: 'Work with our guides to create your daily fishing plan: wade fishing, float trips, lake fishing, or helicopter access to remote rivers.' },
    { name: 'Confirm and Prepare', text: 'Submit deposit to confirm, receive detailed gear list and fly recommendations, and arrange international flights to Chile.' },
    { name: 'Begin Your Adventure', text: 'Arrive in Puerto Montt or Balmaceda, meet our transfer team, and travel to your lodge for a week of world-class Patagonian fishing.' }
  ]
};

export const HOWTO_BOOK_SKITOURING = {
  name: 'How to Book a Ski Touring Trip in Chile',
  description: 'Guide to planning your backcountry ski touring adventure in the Chilean Andes with expert guides.',
  totalTime: 'P14D',
  estimatedCost: { currency: 'USD', value: '5000-10000' },
  steps: [
    { name: 'Assess Your Fitness', text: 'Ski touring requires good cardiovascular fitness and advanced skiing ability. Plan conditioning 2-3 months before your trip.' },
    { name: 'Choose Your Dates', text: 'Select dates between June and October. Early season (June-July) offers cold powder; late season (September-October) provides stable weather and corn snow.' },
    { name: 'Contact Apex', text: 'Share your touring experience, fitness level, and objectives. Our team matches you with appropriate terrain and guides.' },
    { name: 'Customize Your Itinerary', text: 'Design your ideal mix: full ski touring days, combination with heli-accessed touring, or adding resort days for variety.' },
    { name: 'Gear Preparation', text: 'Confirm your touring setup with our team. We provide avalanche safety gear; you bring personal touring equipment or arrange rentals.' },
    { name: 'Arrive Ready to Tour', text: 'Meet in Santiago, transfer to your base lodge, complete avalanche safety review, and begin exploring the Andes backcountry.' }
  ]
};

// ============================================
// Event Schema Component
// For upcoming events and experiences
// ============================================

export interface EventSchemaProps {
  name: string;
  description: string;
  startDate: string;
  endDate?: string;
  location?: string;
  image?: string;
  url?: string;
  eventType?: string;
  organizer?: string;
}

export function EventSchema({
  name,
  description,
  startDate,
  endDate,
  location,
  image,
  url,
  eventType = 'SportsEvent',
  organizer = SEO.siteName,
}: EventSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': eventType,
    name,
    description,
    startDate,
    ...(endDate && { endDate }),
    location: location ? {
      '@type': 'Place',
      name: location,
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'CL',
      },
    } : undefined,
    ...(image && { image }),
    ...(url && { url: `${BASE_URL}${url}` }),
    organizer: {
      '@type': 'Organization',
      name: organizer,
      url: BASE_URL,
    },
    performer: {
      '@type': 'Organization',
      name: organizer,
    },
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/LimitedAvailability',
      url: `${BASE_URL}/contact/`,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Helper function to convert "June 2025" to ISO date
function parseEventDate(dateStr: string): string {
  const months: Record<string, string> = {
    January: '01',
    February: '02',
    March: '03',
    April: '04',
    May: '05',
    June: '06',
    July: '07',
    August: '08',
    September: '09',
    October: '10',
    November: '11',
    December: '12',
  };
  const parts = dateStr.split(' ');
  const month = months[parts[0]] || '01';
  const year = parts[1] || '2025';
  return `${year}-${month}-01`;
}

// Multiple Events Schema for events page
export function EventsListSchema({ events }: { events: Array<{ title: string; description: string; date: string; category: string; image?: string }> }) {
  const schemas = events.map((event) => ({
    '@context': 'https://schema.org',
    '@type': event.category === 'Fishing' ? 'Event' : 'SportsEvent',
    name: event.title,
    description: event.description,
    startDate: parseEventDate(event.date),
    location: {
      '@type': 'Place',
      name: 'Chile',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'CL',
      },
    },
    ...(event.image && { image: event.image }),
    organizer: {
      '@type': 'Organization',
      name: SEO.siteName,
      url: BASE_URL,
    },
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/LimitedAvailability',
      url: `${BASE_URL}/contact/`,
    },
  }));

  return (
    <>
      {schemas.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </>
  );
}
